const API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';
const STORE_KEY = 'rms-music';

function loadSpotifyApi() {
  if (window.__spotifyApiPromise) return window.__spotifyApiPromise;
  window.__spotifyApiPromise = new Promise((resolve) => {
    const previous = window.onSpotifyIframeApiReady;
    window.onSpotifyIframeApiReady = (api) => {
      if (typeof previous === 'function') previous(api);
      resolve(api);
    };
    const script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    document.head.appendChild(script);
  });
  return window.__spotifyApiPromise;
}

function readState() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeState(state) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch (e) {
    /* storage unavailable */
  }
}

/**
 * Soundtrack for the whole site.
 * - Tries to start on its own; browsers that block sound start it on the visitor's first tap, click or key press.
 * - Moves to another song when one ends, or when "Next song" is pressed.
 * - Remembers the song and position, so it picks up where it left off on the next page.
 * - Turns the site blue while the chosen "blue" song plays.
 */
class MusicPlayer extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.uris = (this.dataset.uris || '').split(',').filter(Boolean);
    if (!this.uris.length) return;
    this.scenes = Object.fromEntries(
      (this.dataset.scenes || '').split(',').filter(Boolean).map((pair) => pair.split('='))
    );
    this.button = this.querySelector('[data-music-toggle]');
    this.nextButton = this.querySelector('[data-music-next]');
    this.text = this.querySelector('[data-music-text]');
    this.bpm = Number(this.dataset.bpm) || 140;
    this.shakeOn = this.dataset.shake === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.target = document.getElementById('main-content') || document.body;
    this.playing = false;
    this.advancing = false;

    const saved = readState();
    this.userPaused = saved.paused === true;
    this.currentUri = this.uris.includes(saved.uri) ? saved.uri : this.uris[Math.floor(Math.random() * this.uris.length)];
    this.resumeAt = this.currentUri === saved.uri && saved.position ? saved.position : 0;
    this.applyLook(this.currentUri);

    this.button.addEventListener('click', () => this.toggle());
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.next());

    this.onFirstGesture = (event) => {
      if (this.contains(event.target)) return;
      this.removeGestureListeners();
      if (!this.userPaused && !this.playing) this.start();
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((type) =>
      window.addEventListener(type, this.onFirstGesture, { capture: true, passive: true })
    );

    this.setupGate(saved);
    window.addEventListener('pagehide', () => this.save());

    loadSpotifyApi().then((api) => {
      api.createController(
        this.querySelector('[data-music-embed]'),
        { uri: this.currentUri, width: 300, height: 80 },
        (controller) => {
          this.controller = controller;
          controller.addListener('playback_update', (event) => this.onUpdate(event.data));
          if (!this.userPaused) this.start();
        }
      );
    });
  }

  // Browsers only allow sound after a visitor interacts, so the first visit of a
  // session opens with a short "Enter" screen. Tapping it starts the music.
  setupGate(saved) {
    const gate = this.querySelector('[data-music-gate]');
    if (!gate || saved.entered || this.userPaused) return;
    gate.hidden = false;
    document.documentElement.classList.add('music-gate-open');
    const close = () => {
      gate.hidden = true;
      document.documentElement.classList.remove('music-gate-open');
      this.entered = true;
    };
    const enterButton = gate.querySelector('[data-music-enter]');
    enterButton.addEventListener('click', () => {
      close();
      this.userPaused = false;
      this.removeGestureListeners();
      this.start();
      this.save();
      setTimeout(() => {
        if (!this.playing) this.classList.add('is-open');
      }, 1800);
    });
    gate.querySelector('[data-music-quiet]').addEventListener('click', () => {
      close();
      this.userPaused = true;
      this.removeGestureListeners();
      this.save();
    });
    requestAnimationFrame(() => enterButton.focus());
  }

  removeGestureListeners() {
    ['pointerdown', 'keydown', 'touchstart'].forEach((type) =>
      window.removeEventListener(type, this.onFirstGesture, { capture: true })
    );
  }

  start() {
    if (!this.controller) return;
    this.controller.play();
    if (this.resumeAt > 1000) {
      const seconds = this.resumeAt / 1000;
      this.resumeAt = 0;
      setTimeout(() => this.controller.seek(seconds), 400);
    }
  }

  toggle() {
    if (this.playing) {
      this.userPaused = true;
      if (this.controller) this.controller.pause();
      this.save();
      return;
    }
    this.userPaused = false;
    this.removeGestureListeners();
    // Some browsers only allow sound from a tap inside the Spotify player itself, so show it.
    this.classList.add('is-open');
    if (this.controller) {
      if (this.started) this.controller.resume();
      else this.start();
    }
  }

  next() {
    if (!this.controller) return;
    const others = this.uris.filter((uri) => uri !== this.currentUri);
    const pool = others.length ? others : this.uris;
    this.currentUri = pool[Math.floor(Math.random() * pool.length)];
    this.userPaused = false;
    this.applyLook(this.currentUri);
    this.controller.loadUri(this.currentUri);
    setTimeout(() => this.controller.play(), 400);
    this.save(0);
  }

  applyLook(uri) {
    const scene = this.scenes[uri] || '';
    const root = document.documentElement;
    root.classList.toggle('theme-blue', scene === 'blue');
    if (scene) root.dataset.scene = scene;
    else delete root.dataset.scene;
  }

  save(position) {
    writeState({
      uri: this.currentUri,
      position: position ?? this.livePosition(),
      paused: this.userPaused,
      entered: this.entered || readState().entered === true,
    });
  }

  livePosition() {
    if (this.position == null) return 0;
    return this.playing ? this.position + (performance.now() - this.positionAt) : this.position;
  }

  onUpdate(data) {
    if (!data) return;
    const playing = !data.isPaused && !data.isBuffering;
    this.position = data.position;
    this.positionAt = performance.now();
    if (playing) this.started = true;

    if (data.duration > 0 && data.position >= data.duration - 400 && !this.advancing) {
      this.advancing = true;
      this.next();
      setTimeout(() => (this.advancing = false), 3000);
      return;
    }

    if (playing !== this.playing && !data.isBuffering) {
      this.playing = playing;
      const label = playing ? this.dataset.labelPause : this.dataset.labelPlay;
      this.button.setAttribute('aria-pressed', String(playing));
      this.button.setAttribute('aria-label', label);
      this.text.textContent = label;
      this.classList.toggle('is-playing', playing);
      if (playing) {
        this.removeGestureListeners();
        this.startBeat();
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.classList.remove('is-open'), 1500);
      }
    }

    const now = performance.now();
    if (!this.lastSave || now - this.lastSave > 1000) {
      this.lastSave = now;
      this.save();
    }
  }

  startBeat() {
    if (!this.shakeOn || this.beating) return;
    this.beating = true;
    const beatMs = 60000 / this.bpm;
    let lastBeat = -1;
    const tick = () => {
      if (!this.playing) {
        this.beating = false;
        return;
      }
      const beat = Math.floor(this.livePosition() / beatMs);
      if (beat !== lastBeat) {
        lastBeat = beat;
        this.target.classList.remove('beat-shake');
        void this.target.offsetWidth;
        this.target.classList.add('beat-shake');
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

if (!customElements.get('s-music-player')) customElements.define('s-music-player', MusicPlayer);
